import { useEffect, useState } from 'react';
import { apiGet } from '../../config/apiClient';
import AtlasIcon from './AtlasIcon';
import AvButton from '../nedi/AvButton';
import './Buttons.css';
import './OnboardingChecklist.css';
import {
  getOnboardingChecklistSteps,
  getOnboardingSourceCards,
  isOnboardingSourceDone,
} from './onboardingCopy';
import {
  dismissOnboardingChecklist,
  isOnboardingChecklistDismissed,
} from './onboardingStorage';
import { t } from '../../i18n';

// Checklist first-run (3 passos + cards de fonte). Dismiss persiste em localStorage.
function OnboardingChecklist({ onImport }) {
  const [visible, setVisible] = useState(() => !isOnboardingChecklistDismissed());
  const [statusMap, setStatusMap] = useState(null);
  const steps = getOnboardingChecklistSteps();
  const sourceCards = getOnboardingSourceCards();

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiGet('/api/integrations/status');
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) setStatusMap(data.status || {});
      } catch {
        if (!cancelled) setStatusMap({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible) return null;

  const handleDismiss = () => {
    dismissOnboardingChecklist();
    setVisible(false);
  };

  const handleOpenSource = (featureKey) => {
    if (onImport) onImport(featureKey);
  };

  return (
    <aside className="av-onboarding-checklist" aria-label={t('web.checklist.a11y')}>
      <header className="av-onboarding-checklist-header">
        <span className="av-eyebrow">{t('web.checklist.eyebrow')}</span>
        <h2 className="av-onboarding-checklist-title">{t('web.checklist.title')}</h2>
        <AvButton
          type="button"
          variant="icon"
          className="av-onboarding-checklist-close"
          onClick={handleDismiss}
        >
          <span className="av-sr-only">{t('web.checklist.close')}</span>
          <AtlasIcon name="x" size={16} />
        </AvButton>
      </header>

      <ol className="av-onboarding-checklist-steps">
        {steps.map((step, index) => (
          <li key={step.id} className="av-onboarding-checklist-step">
            <span className="av-onboarding-checklist-step-num" aria-hidden="true">
              {index + 1}
            </span>
            <div className="av-onboarding-checklist-step-body">
              <strong>{step.title}</strong>
              <p>{step.body}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="av-onboarding-source-cards" role="list" aria-label={t('web.checklist.sources')}>
        {sourceCards.map((card) => {
          const done = isOnboardingSourceDone(card, statusMap);
          return (
            <article
              key={card.id}
              className={`av-onboarding-source-card${done ? ' is-done' : ''}`}
              role="listitem"
              data-testid={`onboarding-source-${card.id}`}
              data-done={done ? 'true' : 'false'}
            >
              <div className="av-onboarding-source-card-head">
                <strong>{card.title}</strong>
                {done ? (
                  <span className="av-onboarding-source-done" aria-label={t('web.checklist.done.a11y')}>
                    {t('web.checklist.done')}
                  </span>
                ) : null}
              </div>
              <p>{card.downloadHint}</p>
              <AvButton
                type="button"
                variant="primary"
                className="av-onboarding-source-cta"
                onClick={() => handleOpenSource(card.featureKey)}
              >
                {card.cta}
              </AvButton>
            </article>
          );
        })}
      </div>

      <AvButton
        type="button"
        variant="ghost"
        className="av-onboarding-checklist-skip"
        onClick={handleDismiss}
        block
      >
        {t('web.checklist.skip')}
      </AvButton>
    </aside>
  );
}

export default OnboardingChecklist;
